import React from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';

type TooltipProps = {
  children?: React.ReactNode;
  icon?: string;
  message: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  // Kept for compatibility only click is supported.
  trigger?: Array<'hover' | 'focus' | 'click'>;
};

const Tooltip: React.FC<TooltipProps> = ({
  children = null,
  icon = 'fas fa-info-circle',
  message,
  placement = 'top',
  trigger = ['click'],
  ...props
}) => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (trigger.some(t => t !== 'click')) {
      // eslint-disable-next-line no-console
      console.warn('Tooltip: only click trigger is supported; ignoring', trigger);
    }
  }, [trigger]);

  const handleToggle = () => setOpen(prev => !prev);
  const handleClose = () => setOpen(false);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <MuiTooltip
        title={message}
        placement={placement}
        arrow
        open={open}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: '#00244D',
              color: '#fff',
              fontSize: { xs: '12px', sm: '14px' },
              lineHeight: { xs: 1.25, sm: 1.4 },
              maxWidth: { xs: '85vw', sm: 360 },
              padding: { xs: '8px 10px', sm: '10px 12px' },
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          },
          arrow: { sx: { color: '#00244D' } },
        }}
      >
        <span
          style={{ display: 'inline-flex', alignItems: 'center' }}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggle(); }}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          {...props}
        >
          {children}
          {icon && <span>&nbsp;<i className={`${icon} info-icon`} aria-hidden="true" /></span>}
        </span>
      </MuiTooltip>
    </ClickAwayListener>
  );
};

export default Tooltip;